import useSWR from 'swr';

import API from 'utils/request';
import { IDetailResponse, IPaginatedResponse, ISimpleListResponse } from 'utils/types';

import { fetcher } from './utils';

export interface ICustomerSummary {
  id: number;
  customer_id: string;
  alternate_id?: string | null;
  full_name?: string | null;
  state?: string;
  deactivation_reason?: string | null;
  joined_at?: string;
  activatable?: boolean;
  request_type?: string;
}

export interface ICustomerDeactivationReason {
  id: number;
  reason: string;
}

export interface ICustomerDetail extends ICustomerSummary {
  personal_properties?: Record<string, any>;
}

export interface ICustomerVoucher {
  id: number;
  name: string;
  merchant_name?: string | null;
  valid_from?: string | null;
  valid_to?: string | null;
  voucher_code?: string | null;
  given_by?: string | null;
  given_to?: string | null;
}

export interface ICustomerVoucherMeta {
  issued_count?: number;
  gifted_count?: number;
  redeemed_count?: number;
  voided_count?: number;
  expired_count?: number;
  total_count?: number;
  count?: number;
  page?: number;
  size?: number;
  per_page?: number;
  current_page?: number;
}

export interface ICustomerLoyaltyProgramTier {
  id: number;
  forced_membership_tier_expires_at: string;
  forced_only: boolean;
  images: {
    url: string;
    section: string;
  }[];
  minimum_accumulation_value: number;
  name: string;
  reason: string;
}

export interface ICustomerLoyaltyProgram {
  id: number;
  state: string;
  name: string;
  customer: {
    id: number;
    user_membership_account_id: number;
    state: string;
    current_state: string;
    forced_state: string;
    forced_state_expires_at: null;
    tier: ICustomerLoyaltyProgramTier;
    next_tier: ICustomerLoyaltyProgramTier;
    forced_tier: ICustomerLoyaltyProgramTier | null;
    current_cycle_tier: ICustomerLoyaltyProgramTier;
    point_balance: number;
    tier_points: number;
    lifetime_point_balance: number;
    total_redeemed_points: number;
    total_points_transfer: number;
    total_points_received: number;
    total_points_earned?: number;
    joined_at: string;
    membership_begins_at: string;
    membership_expires_at: string;
  };
  tiers: ICustomerLoyaltyProgramTier[];
  ordering: number;
  enrolled_customers: number;
  issued_date: string;
  updated_at: string;
  current_loyalty_review_cycle_starts_at: string;
  current_loyalty_review_cycle_ends_at: string;
}

export const useCustomerLoyaltyProgramList = (
  customerId?: number | string | null,
  params?: object
) => {
  const key = customerId
    ? { url: `/v4/dash/customers/${customerId}/loyalty_programs`, params }
    : null;

  const { data, isLoading, error, mutate } = useSWR<IPaginatedResponse<ICustomerLoyaltyProgram>>(
    key,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate };
};

export const useCustomerLoyaltyProgram = (customerId: number, loyaltyProgramId: number) => {
  const { data, isLoading, mutate } = useSWR<IDetailResponse<ICustomerLoyaltyProgram>>(
    `/v4/dash/customers/${customerId}/loyalty_programs/${loyaltyProgramId}`,
    fetcher
  );

  return { data: data?.data, isLoading, mutate };
};

export interface LoyaltyProgramTransaction {
  id: number;
  transaction_id: number;
  event_name: string;
  event_id: null;
  expires_at: null;
  transacted_amount: string;
  transacted_at: string;
  points: number;
  transaction_type: string;
  deleted: false;
  properties: Record<string, any>;
}

export const useCustomerLoyaltyProgramTransactionList = (
  customerId: number,
  loyaltyProgramId: number,
  params
) => {
  const url = `v4/dash/customers/${customerId}/loyalty_programs/${loyaltyProgramId}/loyalty_transactions`;

  const { data, isLoading, mutate } = useSWR<IPaginatedResponse<ICustomerLoyaltyProgram>>(
    { url, params },
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, mutate };
};

export const useCustomerRequestLoyaltyProgram = (
  customerId: number,
  loyaltyProgramId: number,
  params
) => {
  const url = `v4/dash/customers/${customerId}/loyalty_programs/${loyaltyProgramId}/customer_requests`;

  const { data, isLoading, mutate } = useSWR({ url, params: { status: 'pending' } }, fetcher);

  return { data: data?.data, meta: data?.meta, isLoading, mutate };
};

export interface IRuleOutcome {
  id?: number;
  transaction_rule?: {
    id?: number;
    name?: string;
    [key: string]: any;
  } | null;
  state?: string;
  additional_info?: Record<string, any> | number | string | null;
}

export interface ICustomerEventLog {
  id: number;
  event_name: string;
  event_params: Record<string, any>;
  state: string;
  created_at: string;
  updated_at: string;
  processing_started_at?: string | null;
  processing_completed_at?: string | null;
}

export interface ICustomerEventLogDetail extends ICustomerEventLog {
  rule_outcomes?: IRuleOutcome[];
}

export interface ICustomerCashbackTransaction {
  id: number;
  amount: number;
  cashback_rule_id: number;
  cashback_rule_name: string;
  currency: string;
  loyalty_program_id: number;
  points_amount: number;
  transacted_at: string;
  transaction_amount: number;
  transaction_id: number;
}

export const useCustomerCashbackTransactionList = (identifier: string) => {
  const url = `v4/dash/cashback/transactions`;

  const { data, isLoading, error, mutate } = useSWR<
    IPaginatedResponse<ICustomerCashbackTransaction>
  >({ url, params: { identifier } }, fetcher);

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate };
};

export const useCustomerPendingCashbackTransactionList = (
  identifier: string,
  makerCheckerCustomerManagement: boolean
) => {
  const url = `v4/dash/cashback/pending_requests`;

  const { data, isLoading, error, mutate } = useSWR<
    IPaginatedResponse<ICustomerCashbackTransaction>
  >({ url, params: { identifier } }, fetcher);

  if (!makerCheckerCustomerManagement) {
    return { data: [], meta: { total_count: 0 }, isLoading: false, error: null, mutate: () => {} };
  }

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate };
};

export const useCustomerList = (params: Record<string, any> = {}, shouldFetch: boolean = true) => {
  const url = '/v4/dash/customers';
  const key = shouldFetch ? { url, params } : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<
    IPaginatedResponse<ICustomerSummary>
  >(key, fetcher);

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useCustomerDeactivationReasons = (shouldFetch: boolean = true) => {
  const key = shouldFetch ? '/v4/dash/customers/deactivation_reasons' : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<
    ISimpleListResponse<ICustomerDeactivationReason>
  >(key, fetcher);

  return { data: data?.data, isLoading, error, mutate, isValidating };
};

export const useCustomer = (id?: number | string | null, params?: Record<string, any>) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<IDetailResponse<ICustomerDetail>>(
    id ? { url: `/v4/dash/customers/${id}`, params } : null,
    fetcher
  );

  return { data: data?.data, isLoading, error, mutate, isValidating };
};

export const useCustomerMe = (token?: string | null) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR(
    token
      ? {
          url: '/v4/me',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          external: true
        }
      : null,
    fetcher
  );

  const payload = (data as any)?.data ?? data;
  return { data: payload, isLoading, error, mutate, isValidating };
};

export const useCustomerWithToken = (
  customerId?: number | string | null,
  token?: string | null
) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<IDetailResponse<ICustomerDetail>>(
    customerId && token
      ? {
          url: `/v4/dash/customers/${customerId}`,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          external: true
        }
      : null,
    fetcher
  );

  return { data: data?.data, isLoading, error, mutate, isValidating };
};

export const useCustomerEventLogs = (
  customerId: number,
  params: Record<string, any>,
  shouldFetch: boolean = true
) => {
  const { data, isLoading, error, mutate } = useSWR<IPaginatedResponse<ICustomerEventLog>>(
    customerId && shouldFetch
      ? { url: `/v4/dash/customers/${customerId}/event_logs`, params }
      : null,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate };
};

export const useCustomerVoucherListWithToken = (
  customerId?: number | string | null,
  token?: string | null,
  params?: Record<string, any>
) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<{
    data: ICustomerVoucher[];
    meta: ICustomerVoucherMeta;
  }>(
    customerId && token
      ? {
          url: `/v4/dash/customers/${customerId}/vouchers`,
          headers: { Authorization: `Bearer ${token}` },
          params,
          external: true
        }
      : null,
    fetcher
  );

  return {
    data: data?.data,
    meta: data?.meta,
    isLoading,
    error,
    mutate,
    isValidating
  };
};

export const useCustomerEventLog = (
  customerId: number,
  eventLogId: number | null,
  shouldFetch: boolean = true
) => {
  const { data, isLoading, error, mutate } = useSWR<IDetailResponse<ICustomerEventLogDetail>>(
    customerId && eventLogId && shouldFetch
      ? `/v4/dash/customers/${customerId}/event_logs/${eventLogId}`
      : null,
    fetcher
  );

  return { data: data?.data, isLoading, error, mutate };
};

export const useCustomerLoyaltyProgramListWithToken = (
  customerId?: number | string | null,
  token?: string | null,
  params?: Record<string, any>
) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<
    IPaginatedResponse<ICustomerLoyaltyProgram>
  >(
    customerId && token
      ? {
          url: `/v4/dash/customers/${customerId}/loyalty_programs`,
          headers: { Authorization: `Bearer ${token}` },
          params,
          external: true
        }
      : null,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export interface ICustomerTransaction {
  id: number;
  transaction_reference: string;
  amount: number | string;
  created_at: string;
  state: string;
  properties: Record<string, any>;
}

export interface ICustomerTransactionDetail extends ICustomerTransaction {
  user_account_id?: number;
  transaction_type?: string | null;
  transaction_date?: string | null;
  currency?: string | null;
  updated_at?: string;
  processed_at?: string | null;
  process_at?: string | null;
  transaction_status?: string | null;
  unique_transaction_id?: string | null;
  rule_outcomes?: IRuleOutcome[];
  [key: string]: any;
}

export const useCustomerTransactions = (
  customerId: number,
  params: Record<string, any>,
  shouldFetch: boolean = true
) => {
  const { data, isLoading, error, mutate } = useSWR<IPaginatedResponse<ICustomerTransaction>>(
    customerId && shouldFetch
      ? { url: `/v4/dash/customers/${customerId}/transactions`, params }
      : null,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate };
};

export const useCustomerTransaction = (
  customerId: number,
  transactionId: number | null,
  shouldFetch: boolean = true
) => {
  const { data, isLoading, error, mutate } = useSWR<IDetailResponse<ICustomerTransactionDetail>>(
    customerId && transactionId && shouldFetch
      ? `/v4/dash/customers/${customerId}/transactions/${transactionId}`
      : null,
    fetcher
  );

  return { data: data?.data, isLoading, error, mutate };
};

export interface IFilteringCheckResult {
  passed_filters: { name: string }[];
  failed_filters: { name: string }[];
}

export const getRewardFilteringChecks = (customerId: number | string, rewardId: number | string) =>
  API.get<{ data: IFilteringCheckResult }>(
    `/v4/dash/rewards/${rewardId}/customer_filtering/${customerId}`
  );

export const getCampaignFilteringChecks = (
  customerId: number | string,
  campaignId: number | string
) =>
  API.get<{ data: IFilteringCheckResult }>(
    `/v4/dash/campaigns/${campaignId}/customer_filtering/${customerId}`
  );

export interface ICustomerCreditPointLimit {
  id: number;
  period?: string;
  transaction_property_value?: string;
  total_credit_point_limit?: number;
  available_credit_point_limit?: number;
}

export const useCustomerCreditPointLimitList = (
  customerId?: number | string | null,
  loyaltyProgramId?: number | string | null
) => {
  const key =
    customerId && loyaltyProgramId
      ? `/v4/dash/customers/${customerId}/loyalty_programs/${loyaltyProgramId}/credit_point_limits`
      : null;

  const { data, isLoading, error, mutate, isValidating } = useSWR<{
    data: ICustomerCreditPointLimit[];
  }>(key, fetcher);

  return { data: data?.data, isLoading, error, mutate, isValidating };
};
